import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useLegacyBallAuthorization } from '../hooks/useLegacyBallAuthorization';
import { usePageAuthorization } from '../hooks/usePageAuthorization';
import { DownloadDataTab } from '../components/legacy-ball/DownloadDataTab';
import { LegacyBallInfoView, LegacyBallInfoResponse } from '../components/legacy-ball/LegacyBallInfoView';
import { LegacyBallSettingsView } from '../components/legacy-ball/LegacyBallSettingsView';
import { RegistrationsTab } from '../components/legacy-ball/RegistrationsTab';
import { TableManagementTab } from '../components/legacy-ball/table-management/TableManagementTab';
import { TicketInventoryTab } from '../components/legacy-ball/TicketInventoryTab';
import { TicketSalesProgressCard } from '../components/legacy-ball/TicketSalesProgressCard';
import { GuestManagementView } from '../components/legacy-ball/GuestManagementView';
import { TableReservationRequests } from '../components/legacy-ball/TableReservationRequests';

type ActiveTab = 'information' | 'settings' | 'registrations' | 'table-management' | 'ticket-inventory' | 'guests' | 'download-data';

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0' },
  secondaryButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#1a6b3c',
    backgroundColor: '#fff',
    border: '1px solid #1a6b3c',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  tabBar: {
    display: 'flex',
    gap: '0',
    marginBottom: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#666',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  tabActive: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#1a6b3c',
    borderBottom: '2px solid #1a6b3c',
    marginBottom: '-2px',
    transition: 'color 0.2s, border-color 0.2s',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '1rem',
  },
};

export function LegacyBallPage() {
  const { user } = useAuth();
  const { isAuthorized } = useLegacyBallAuthorization();
  const { canEdit: pageCanEdit } = usePageAuthorization('legacy-ball');
  const canEdit = isAuthorized || pageCanEdit;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['information', 'settings', 'registrations', 'table-management', 'ticket-inventory', 'guests', 'download-data'].includes(tabParam)) {
      return tabParam as ActiveTab;
    }
    return 'information';
  });
  const [eventInfo, setEventInfo] = useState<LegacyBallInfoResponse | null>(null);
  const [guestSubView, setGuestSubView] = useState<'management' | 'table-requests'>('management');
  const [infoError, setInfoError] = useState('');
  const [infoLoading, setInfoLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetchEventInfo() {
      try {
        const data = await apiClient<LegacyBallInfoResponse>('/legacy-ball/info');
        if (!cancelled) {
          setEventInfo(data);
        }
      } catch {
        if (!cancelled) {
          setInfoError('Failed to load event information.');
        }
      } finally {
        if (!cancelled) {
          setInfoLoading(false);
        }
      }
    }

    fetchEventInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Legacy Ball</h1>
        {user && ['Admin', 'Organizer'].includes(user.role) && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              style={styles.secondaryButton}
              onClick={() => navigate('/qr-generator')}
            >
              🎫 QR Generator
            </button>
            <button
              style={styles.secondaryButton}
              onClick={() => navigate('/registration-linking')}
            >
              Registration Linking
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div style={styles.tabBar}>
        <button
          style={activeTab === 'information' ? styles.tabActive : styles.tab}
          onClick={() => setActiveTab('information')}
        >
          Information
        </button>
        {canEdit && (
          <button
            style={activeTab === 'settings' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        )}
        {canEdit && (
          <button
            style={activeTab === 'registrations' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('registrations')}
          >
            Registrations
          </button>
        )}
        {canEdit && (
          <button
            style={activeTab === 'table-management' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('table-management')}
          >
            Table Management
          </button>
        )}
        {canEdit && (
          <button
            style={activeTab === 'ticket-inventory' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('ticket-inventory')}
          >
            Ticket Inventory
          </button>
        )}
        {canEdit && (
          <button
            style={activeTab === 'guests' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('guests')}
          >
            👥 Guests
          </button>
        )}
        {canEdit && (
          <button
            style={activeTab === 'download-data' ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab('download-data')}
          >
            📥 Download Data
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'information' && (
        <>
          {infoError && <div style={styles.error}>{infoError}</div>}
          {infoLoading ? (
            <div style={styles.loading}>Loading event information...</div>
          ) : (
            <>
              <TicketSalesProgressCard />
              <div style={{ marginTop: '1.5rem' }}>
                <LegacyBallInfoView data={eventInfo} />
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'settings' && canEdit && (
        <LegacyBallSettingsView user={user} />
      )}

      {activeTab === 'registrations' && canEdit && (
        <RegistrationsTab />
      )}

      {activeTab === 'table-management' && canEdit && (
        <TableManagementTab />
      )}

      {activeTab === 'ticket-inventory' && canEdit && (
        <TicketInventoryTab />
      )}

      {activeTab === 'guests' && canEdit && (
        <>
          <nav style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <button onClick={() => setGuestSubView('management')} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: guestSubView === 'management' ? '#1a6b3c' : '#f5f5f5', color: guestSubView === 'management' ? '#fff' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Guest Management
            </button>
            <button onClick={() => setGuestSubView('table-requests')} style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: guestSubView === 'table-requests' ? '#1a6b3c' : '#f5f5f5', color: guestSubView === 'table-requests' ? '#fff' : '#6b7280', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Table Requests
            </button>
          </nav>
          {guestSubView === 'management' && <GuestManagementView />}
          {guestSubView === 'table-requests' && <TableReservationRequests />}
        </>
      )}

      {activeTab === 'download-data' && canEdit && (
        <DownloadDataTab />
      )}
    </div>
  );
}
